import React from "react";
import {Form} from "react-bootstrap";
import {ReactSelectBootstrap} from "react-select-bootstrap";
import SelectList from "../SelectList.jsx";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import ArtikelFilter from "./ArtikelFilter.jsx";

const POOL_OPTION = {value: "pool", label: "Nicht zugeordnet – prüfen"};
const NONE_SECTOR_OPTION = {value: "none", label: "Ohne Branche"};

function toRepOption(rep) {
    return {value: rep.code, label: rep.is_former ? `${rep.name} (ehemalig)` : rep.name};
}

function toArticleTypeOption(item) {
    if (typeof item === "string") {
        return {value: item, label: item};
    }
    return {value: item.value ?? item.name ?? item.id, label: item.label ?? item.name ?? String(item)};
}

function sectorOptions(meta) {
    const list = (meta?.sectors || []).map((s) => ({value: String(s.id), label: s.name}));
    return list.some((o) => o.value === "none") ? list : [...list, NONE_SECTOR_OPTION];
}

/**
 * Nur Kürzel mit Daten (mindestens ein zugeordneter Kunde mit Umsatz im
 * Standardzeitraum, `has_data`) - `has_data === false` ist der einzige Wert,
 * der einen Vertreter ausschließt; fehlt das Feld (Backend liefert es noch
 * nicht), bleibt der Vertreter wie bisher wählbar.
 */
function repsWithData(meta) {
    return (meta?.reps || []).filter((rep) => rep.has_data !== false);
}

function repOptions(meta) {
    return [POOL_OPTION, ...repsWithData(meta).map(toRepOption)];
}

function yearOptions(meta) {
    if (!meta?.year_min || !meta?.year_max) {
        return [];
    }
    const years = [];
    for (let year = meta.year_max; year >= meta.year_min; year -= 1) {
        years.push({value: String(year), label: String(year)});
    }
    return years;
}

const MONTH_OPTIONS = Array.from({length: 12}, (_, i) => ({value: String(i + 1), label: String(i + 1)}));

/**
 * Gemeinsame Filterleiste der Vertriebsauswertung: Zeitraum (Jahr von/bis),
 * Stichmonat, Branche, Vertreter, Artikelart, Produktlinie, Farbton/Artikel
 * und der Schalter
 * "Fracht ausblenden". Nimmt bewusst den kompletten Rückgabewert von
 * useAnalysisFilters() entgegen (siehe FilterBar.jsx der Verkaufsstatistik
 * für dasselbe Muster).
 */
export default function AnalysisFilterBar({filters, showReps = true, showSectors = true}) {
    const {
        meta,
        yearFrom, setYearFrom,
        yearTo, setYearTo,
        stichmonat, setStichmonat,
        referenceComplete,
        sectors, setSectors,
        reps, setReps,
        articleTypes, setArticleTypes,
        productLines, setProductLines,
        articles, setArticles,
        withoutFreight, setWithoutFreight,
    } = filters;

    const years = yearOptions(meta);
    const selectedSectors = sectorOptions(meta).filter((o) => sectors.includes(o.value));
    const selectedReps = repOptions(meta).filter((o) => reps.includes(o.value));
    const articleTypeOptionList = (meta?.article_types || []).map(toArticleTypeOption);
    const selectedArticleTypes = articleTypeOptionList.filter((o) => articleTypes.includes(o.value));
    const productLineOptionList = (meta?.product_lines || []).map((p) => ({value: p.name, label: p.name}));
    const selectedProductLines = productLineOptionList.filter((o) => productLines.includes(o.value));

    return (
        <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
            <div className="d-flex align-items-end gap-2">
                <div>
                    <Form.Label className="small text-muted mb-1" htmlFor="va-year-from">
                        <HeaderWithHelp help="Erstes Jahr des angezeigten Zeitraums.">Jahr von</HeaderWithHelp>
                    </Form.Label>
                    <SelectList
                        id="va-year-from"
                        size="sm"
                        value={yearFrom}
                        options={years}
                        onChange={setYearFrom}
                        style={{width: 100}}
                    />
                </div>
                <div>
                    <Form.Label className="small text-muted mb-1" htmlFor="va-year-to">
                        <HeaderWithHelp help="Letztes Jahr des angezeigten Zeitraums (Bezugsjahr); bestimmt, ob Hochrechnung und Stichmonat greifen.">
                            Jahr bis
                        </HeaderWithHelp>
                    </Form.Label>
                    <SelectList
                        id="va-year-to"
                        size="sm"
                        value={yearTo}
                        options={years}
                        onChange={setYearTo}
                        style={{width: 100}}
                    />
                </div>
            </div>

            <div>
                <Form.Label className="small text-muted mb-1" htmlFor="va-stichmonat">
                    <HeaderWithHelp
                        help={
                            referenceComplete
                                ? "Letzter vollständig importierter Monat. Bestimmt, bis wohin das laufende Jahr zählt und durch welche Zahl die Hochrechnung teilt. Ohne Wirkung, weil der Zeitraum mit einem abgeschlossenen Jahr endet."
                                : "Letzter vollständig importierter Monat. Bestimmt, bis wohin das laufende Jahr zählt und durch welche Zahl die Hochrechnung teilt."
                        }
                    >
                        Stichmonat
                    </HeaderWithHelp>
                </Form.Label>
                <SelectList
                    id="va-stichmonat"
                    size="sm"
                    value={stichmonat}
                    options={MONTH_OPTIONS}
                    onChange={setStichmonat}
                    disabled={referenceComplete}
                    style={{width: 80}}
                />
            </div>

            {showSectors && (
                <div style={{minWidth: 200, flex: "1 1 200px", maxWidth: 300}}>
                    <Form.Label className="small text-muted mb-1">
                        <HeaderWithHelp help="Aus dem weclapp-Kundenstamm, aktueller Stand, gilt rückwirkend für alle Jahre. Standard wie in der Excel-Auswertung: Graffiti und Fine Arts.">
                            Branche
                        </HeaderWithHelp>
                    </Form.Label>
                    <ReactSelectBootstrap
                        isMulti
                        placeholder="Branchen"
                        noOptionsMessage={() => "Keine Branche gefunden"}
                        options={sectorOptions(meta)}
                        value={selectedSectors}
                        onChange={(value) => setSectors((value || []).map((o) => o.value))}
                    />
                </div>
            )}

            {showReps && (
                <div style={{minWidth: 200, flex: "1 1 200px", maxWidth: 300}}>
                    <Form.Label className="small text-muted mb-1">
                        <HeaderWithHelp help="Kundenverantwortlicher in weclapp, über den Benutzer dem Kürzel zugeordnet (Stammdaten).">
                            Vertreter
                        </HeaderWithHelp>
                    </Form.Label>
                    <ReactSelectBootstrap
                        isMulti
                        placeholder="Alle Vertreter"
                        noOptionsMessage={() => "Kein Vertreter gefunden"}
                        options={repOptions(meta)}
                        value={selectedReps}
                        onChange={(value) => setReps((value || []).map((o) => o.value))}
                    />
                </div>
            )}

            <div style={{minWidth: 200, flex: "1 1 200px", maxWidth: 300}}>
                <Form.Label className="small text-muted mb-1">
                    <HeaderWithHelp help='Artikelart nach Zuordnung des Artikelstamms (Marker Systems, Spray Paint, OEM, Andere; "Nicht zugeordnet" ohne Zuordnung).'>
                        Artikelart
                    </HeaderWithHelp>
                </Form.Label>
                <ReactSelectBootstrap
                    isMulti
                    placeholder="Alle Artikelarten"
                    noOptionsMessage={() => "Keine Artikelart gefunden"}
                    options={articleTypeOptionList}
                    value={selectedArticleTypes}
                    onChange={(value) => setArticleTypes((value || []).map((o) => o.value))}
                />
            </div>

            <div style={{minWidth: 220, flex: "1 1 220px", maxWidth: 320}}>
                <Form.Label className="small text-muted mb-1">
                    <HeaderWithHelp help="Produktlinie des Artikels laut Artikelstamm.">Produktlinie</HeaderWithHelp>
                </Form.Label>
                <ReactSelectBootstrap
                    isMulti
                    placeholder="Alle Produktlinien"
                    noOptionsMessage={() => "Keine Produktlinie gefunden"}
                    options={productLineOptionList}
                    value={selectedProductLines}
                    onChange={(value) => setProductLines((value || []).map((o) => o.value))}
                />
            </div>

            <ArtikelFilter value={articles} onChange={setArticles} />

            <div className="d-flex align-items-center gap-1 mb-1">
                <Form.Check
                    type="switch"
                    id="va-without-freight"
                    label="Fracht ausblenden"
                    checked={withoutFreight}
                    onChange={(event) => setWithoutFreight(event.target.checked)}
                    className="mb-0"
                />
                <HeaderWithHelp help="Blendet Frachtpositionen (Produktlinie ‚FRACHKOSTEN') aus. Sie stammen aus den Majesty-Rechnungen bis Juni 2026; weclapp-Rechnungen enthalten keine Frachtpositionen. Die bisherige Excel-Auswertung zählt Fracht mit." />
            </div>
        </div>
    );
}
